<script lang="ts" setup>
import { useRouter } from "vue-router";
import { computed, nextTick, onBeforeMount, onMounted, ref } from "vue";
import { useToast } from "vue-toastification";
import { Quiz } from "@/models/Quiz";
import { QuizQuestion } from "@/models/QuizQuestion";
import { QuizAnswer } from "@/models/QuizAnswer";
import { progress } from "./composables/progress";
import config from "@/config.json";
import quizData from "@/assets/quiz/data.json";
import HeaderComponent from "@/components/HeaderComponent.vue";
import LoadingComponent from "@/components/LoadingComponent.vue";
import FooterComponent from "@/components/FooterComponent.vue";

const router = useRouter();
const quizzes = ref<Quiz[]>(quizData as Array<Quiz>);
const isLoading = ref(true);
const overlayIsVisible = ref(false);
const hasProgress = computed(() => {
  const hasCompletedQuizzes = quizzes.value.filter(
    (quiz: Quiz) => quiz.completed === true
  );

  const hasQuizProgress = quizzes.value.filter((quiz: Quiz) => {
    return quiz.questions?.some((question: QuizQuestion) => {
      return question.answers?.some((answer: QuizAnswer) => {
        return answer.selected === true;
      });
    });
  });

  return hasCompletedQuizzes.length > 0 || hasQuizProgress.length > 0;
});

onBeforeMount(() => {
  const toast = useToast();

  // Set quiz question & answer ids
  quizzes.value.forEach((quiz) => {
    quiz.questions?.forEach((question, questionIndex) => {
      question['id'] = questionIndex;

      question.answers?.forEach((answer, answerIndex) => {
        answer['id'] = answerIndex;
      });
    });
  });

  // Sort quizzes
  quizzes.value.sort((a, b) =>
    (typeof a.comingSoon !== "undefined" && a.comingSoon) ===
    (typeof b.comingSoon !== "undefined" && b.comingSoon)
      ? 0
      : typeof a.comingSoon !== "undefined" && a.comingSoon
      ? 1
      : -1
  );

  // Preload images
  quizzes.value.forEach((quiz) => {
    quiz.questions?.forEach((question) => {
      quiz.completed = false;
      if (!question.image) {
        return;
      }

      const image = new Image();
      image.src = require(`@/assets/quiz/images/${quiz.id}/${question.image}`);
    });
  });

  // Get quizzes from local storage
  const quizzesInLocalStorage = localStorage.getItem('dive-quizzes');
  const _quizzes = quizzesInLocalStorage && JSON.parse(quizzesInLocalStorage);

  // Version unchanged, set quizzes from local storage
  if (_quizzes !== null && _quizzes.version === config.version) {
    // Single quiz version has changed
    let toastTimer = 0;
    const updated = _quizzes.quizzes.map((_quiz: Quiz, quizIndex: number) => {
      if (_quiz.version !== null && _quiz.version !== quizzes.value[quizIndex].version && !quizzes.value[quizIndex].comingSoon) {
        toastTimer++;

        setTimeout(() => { 
          nextTick(() => {
          toast.success(`Wir haben das Quiz "${quizzes.value[quizIndex].name}" aktualisiert. Dein Fortschritt wurde zurückgesetzt.`, {
            id: `quiz-${quizzes.value[quizIndex].id}-version-changed`,
          });
        });
        }, toastTimer * 1000);

        return quizzes.value[quizIndex];
      }

      // Single quiz version hasn't changed
      return {
        ..._quiz,
        name: quizzes.value[quizIndex].name,
        description: quizzes.value[quizIndex].description,
        questions: _quiz.questions.map((question: QuizQuestion, questionIndex: number) => {
          return {
            ...question,
            text: quizzes.value[quizIndex].questions[questionIndex].text,
            explanation: quizzes.value[quizIndex].questions[questionIndex].explanation || null,
            answers: question.answers.map((answer: QuizAnswer) => {
              const index = quizzes.value[quizIndex].questions[questionIndex].answers.findIndex((a) => {
                return a.id === answer.id;
              });
              
              return {
                ... quizzes.value[quizIndex].questions[questionIndex].answers[index],
                selected: answer.selected
              };
            }),
          };
        }),
      };
    });

    quizzes.value = updated;
  } else if (_quizzes !== null && _quizzes.version !== config.version) { // App version changed
    nextTick(() => {
      toast.success("Wir haben die Quiz-App aktualisiert. Dein Fortschritt wurde zurückgesetzt.", {
        id: "app-version-changed",
      });
    });
  }

  persistInLocalStorage();
});

onMounted(async () => {
  setIsLoading(false);

  // Prevent document scroll on keydown "space"
  window.addEventListener("keydown", (e) => {
    if (
      (e.key === " " || e.key === "Spacebar") &&
      (e.target as HTMLElement)?.tagName !== "INPUT"
    ) {
      e.preventDefault();
    }
  });
});

const getQuiz = (id: string) => {
  return quizzes.value?.find((quiz) => quiz.id === id);
};

const setQuiz = (event: string) => {
  const quiz = getQuiz(event);

  if (quiz && !quiz.comingSoon) {
    router.push({
      name: "quiz",
      params: {
        id: quiz.id,
        questionIndex: 0,
      },
    });
  }
};

const selectAnswer = (event: {
  quizId: string;
  questionIndex: number;
  answerIndex: number;
}) => {
  const quiz = getQuiz(event.quizId);

  if (!quiz) {
    return;
  }

  const answer = quiz.questions[event.questionIndex].answers[event.answerIndex];

  if (answer?.selected) {
    quiz.questions[event.questionIndex].answers[event.answerIndex].selected =
      false;
  } else {
    if (isSingleChoice(quiz.questions[event.questionIndex])) {
      quiz.questions[event.questionIndex].answers.forEach(
        (answer: QuizAnswer) => (answer.selected = false)
      );
    }

    answer.selected = true;
  }

  quiz.progress = progress(quiz);

  updateScore(quiz);
};

const isSingleChoice = (question: QuizQuestion) => {
  const correctAnswers = question.answers?.filter((answer) => answer.correct);

  return correctAnswers.length === 1;
};

const updateScore = (quiz: Quiz) => {
  let score = 0;

  quiz.questions?.forEach((question) => {
    let questionScoreInPercent;

    let correctAnswers = 0;
    let correctAnswersSelected = 0;

    question.answers?.forEach((answer) => {
      if (answer.selected && answer.correct) {
        correctAnswers++;
        correctAnswersSelected++;
      } else if (answer.selected && !answer.correct) {
        correctAnswersSelected -= 0.4;
      } else if (!answer.selected && answer.correct) {
        correctAnswers++;
        correctAnswersSelected--;
      }
    });

    correctAnswersSelected =
      correctAnswersSelected < 0 ? 0 : correctAnswersSelected;

    questionScoreInPercent = (100 / correctAnswers) * correctAnswersSelected;
    score += questionScoreInPercent;
  });

  const _quiz = getQuiz(quiz.id);

  if (_quiz) {
    _quiz.score =
      quiz.questions?.length > 0
        ? (score / quiz.questions.length / 10) * 10
        : 0;

    persistInLocalStorage();
  }
};

const persistInLocalStorage = () => {
  const quizzesObj = {
    version: config.version,
    quizzes: quizzes.value,
  };

  localStorage.setItem("dive-quizzes", JSON.stringify(quizzesObj));
};

const reset = () => {
  setIsLoading(true);

  quizzes.value.forEach((quiz) => {
    quiz.completed = false;

    quiz.questions?.forEach((question) => {
      question.answers?.forEach((answer) => {
        answer.selected = false;
      });
    });
  });

  quizzes.value.forEach((quiz) => updateScore(quiz));

  localStorage.removeItem("dive-quizzes");

  setIsLoading(false);

  const toast = useToast();

  toast.success("Dein Quiz-Fortschritt wurde zurückgesetzt.", {
    id: "well-done",
  });
};

const setIsLoading = (event: boolean) => {
  if (event) {
    isLoading.value = true;
    return;
  }

  setTimeout(() => {
    isLoading.value = false;
  }, 500);
};

const toggleOverlay = (event: boolean) => {
  overlayIsVisible.value = event;
};
</script>

<template>
  <LoadingComponent v-if="isLoading" />
  <HeaderComponent />
  <main>
    <router-view
      :quizzes="quizzes"
      :overlayIsVisible="overlayIsVisible"
      :hasProgress="hasProgress"
      v-slot="{ Component }"
      v-on:reset="reset()"
      v-on:setQuiz="setQuiz($event)"
      v-on:updateScore="updateScore($event)"
      v-on:selectAnswer="selectAnswer($event)"
      v-on:setIsLoading="setIsLoading($event)"
      v-on:toggleOverlay="toggleOverlay($event)"
    >
      <transition name="fade" mode="out-in">
        <component :is="Component" />
      </transition>
    </router-view>
    <FooterComponent />
  </main>
</template>

<style lang="scss">
@import "@/assets/scss/app.scss";
</style>
